import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, UserPlus, Trash, Copy, ArrowClockwise, Eye, EyeSlash, Key, Flask, PaperPlaneTilt, ShuffleAngular, Globe, IdentificationCard, Broadcast } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LandingPageAllowlistPicker from "@/components/shared/LandingPageAllowlistPicker";
import SetPasswordDialog from "@/components/SetPasswordDialog";
import IntegrationKitPreview from "@/components/IntegrationKitPreview";
import TrafficSourcesPanel from "@/components/shared/TrafficSourcesPanel";
import { MarketplaceCommissionField } from "@/components/affiliate/MarketplaceCommissionField";
import { AllPagesToggle } from "@/components/traffic/AllPagesToggle";

// Iter-68 — profile fields the admin can edit on an existing affiliate.
// Kept in one place so the load / save / dirty-check logic reads the
// same list. `notes` is a textarea; everything else renders as an input.
const PROFILE_FIELDS = ["name", "contact_email", "contact_phone", "website", "notes"];
const _blankProfile = () => ({
  name: "", contact_email: "", contact_phone: "", website: "", notes: "",
});

// Presentational row for one api key. Kept outside AffiliateManageModal so
// react/no-unstable-nested-components is satisfied.
function KeyRow({ label, value, revealed, onToggle, onCopy, onRotate, busy, tint, testId }) {
  return (
    <div className="border border-black p-3 bg-zinc-50" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${tint}`}>
          {label}
        </div>
        <div className="label-mono ml-auto">{revealed ? "revealed" : "hidden"}</div>
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          type={revealed ? "text" : "password"}
          value={value || ""}
          className="w-full border border-black px-3 py-2 font-mono text-[11px] bg-white"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
          className="border border-black p-2 hover:bg-black hover:text-white"
          title={revealed ? "Hide" : "Reveal"}
        >
          {revealed ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
        </button>
        <button
          type="button"
          onClick={() => onCopy(label, value)}
          data-testid={`${testId}-copy`}
          className="border border-black p-2 hover:bg-black hover:text-white"
          title="Copy to clipboard"
        >
          <Copy size={12} weight="bold" />
        </button>
        <button
          type="button"
          onClick={onRotate}
          disabled={busy}
          data-testid={`${testId}-rotate`}
          className="border border-red-700 text-red-800 px-2 py-2 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
          title="Mint a new key — old key stops working immediately"
        >
          <ArrowClockwise size={11} weight="bold" /> Rotate
        </button>
      </div>
    </div>
  );
}

/**
 * Iter-30.6: Affiliate api-key & vendor management modal.
 *
 * Shown on the /affiliates page from a per-row "Manage" button. Super_admin
 * only can:
 *  - Reveal + rotate the LIVE api_key.
 *  - Reveal + rotate the SANDBOX api_key.
 *  - Invite a vendor login (read-only /vendor dashboard).
 *  - See / unlink existing vendor users.
 */
export default function AffiliateManageModal({ affiliate, onClose, onSaved }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [keys, setKeys] = useState(null);
  const [reveal, setReveal] = useState({ live: false, sandbox: false });
  const [vendors, setVendors] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter-30.7: state cost editor state
  const [costsText, setCostsText] = useState("");
  const [savedCosts, setSavedCosts] = useState({});
  // Iter-32: integration kit email state
  const [kitEmail, setKitEmail] = useState("");
  // Iter-33: A/B split toggle state
  const [abSplit, setAbSplit] = useState(false);
  // Iter-66: per-affiliate landing-page allowlist. `slugs` is the
  // in-flight draft (what the picker mutates); `savedSlugs` is the
  // last-persisted value, used to render the "Save" button as
  // enabled/disabled without a deep-equal on every keystroke.
  const [slugs, setSlugs] = useState([]);
  const [savedSlugs, setSavedSlugs] = useState([]);
  const [allPages, setAllPages] = useState(false);
  const [savingSlugs, setSavingSlugs] = useState(false);
  // Iter-68: editable profile / contact fields. `profile` is what the
  // form holds; `savedProfile` is the last-persisted set — a shallow
  // compare gates the Save button so we never PATCH an unchanged row.
  const [profile, setProfile] = useState(() => ({ ..._blankProfile(),
    name: affiliate.name || "",
    contact_email: affiliate.contact_email || "",
    contact_phone: affiliate.contact_phone || "",
    website: affiliate.website || "",
    notes: affiliate.notes || "",
  }));
  const [savedProfile, setSavedProfile] = useState(profile);
  const [savingProfile, setSavingProfile] = useState(false);
  // Iter-69 — target of the "Set password" dialog on the Vendor logins
  // list. `null` = dialog closed. The dialog itself lives in the shared
  // `SetPasswordDialog` component so this modal doesn't grow another
  // form-state island.
  const [pwTarget, setPwTarget] = useState(null);

  const loadKeys = async () => {
    try {
      const { data } = await api.get(`/organizations/${affiliate.id}/api-keys`);
      setKeys(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot load api keys");
    }
  };
  const loadVendors = async () => {
    try {
      const { data } = await api.get(`/organizations/${affiliate.id}/vendors`);
      setVendors(data.vendors || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot load affiliate logins");
    }
  };
  const loadCosts = async () => {
    try {
      const { data } = await api.get(`/organizations/${affiliate.id}/state-costs`);
      setSavedCosts(data.state_costs || {});
      setCostsText(
        Object.entries(data.state_costs || {})
          .sort()
          .map(([s, c]) => `${s}:${c}`)
          .join(", "),
      );
    } catch (err) {
      // silent — this is admin-only and legacy affiliates might not have it yet
    }
  };
  // Iter-33: fetch A/B toggle current state on modal open
  const loadAbSplit = async () => {
    try {
      const { data } = await api.get(`/organizations/${affiliate.id}/ab-split`);
      setAbSplit(!!data.ab_split_enabled);
    } catch (err) { /* silent */ }
  };
  // Iter-66 + Iter-68: single fetch of the org doc hydrates BOTH the
  // approved-landing-page allowlist and the editable profile / contact
  // fields. Merging the two loads avoids double round-trips on modal
  // open and keeps `savedProfile` in lock-step with `savedSlugs`.
  const loadOrg = async () => {
    try {
      const { data } = await api.get(`/organizations/${affiliate.id}`);
      const cur = Array.isArray(data?.allowed_landing_page_slugs)
        ? data.allowed_landing_page_slugs
        : [];
      setSlugs(cur);
      setSavedSlugs(cur);
      setAllPages(Boolean(data?.traffic_all_pages));
      const p = {
        name: data?.name || "",
        contact_email: data?.contact_email || "",
        contact_phone: data?.contact_phone || "",
        website: data?.website || "",
        notes: data?.notes || "",
      };
      setProfile(p);
      setSavedProfile(p);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { loadKeys(); loadVendors(); loadCosts(); loadAbSplit(); loadOrg(); }, [affiliate.id]);

  const copy = (label, val) => {
    navigator.clipboard.writeText(val || "");
    toast.success(`${label} copied to clipboard`);
  };

  const rotateSandbox = async () => {
    if (!confirm(
      "Rotate the SANDBOX api key? The old sandbox key stops working immediately."
    )) return;
    setBusy(true);
    try {
      await api.post(`/organizations/${affiliate.id}/mint-sandbox-key`);
      toast.success("Sandbox key rotated");
      await loadKeys();
      setReveal((r) => ({ ...r, sandbox: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rotate failed");
    } finally { setBusy(false); }
  };

  const rotateLive = async () => {
    if (!confirm(
      "Rotate the LIVE api key? The old key stops working immediately — any affiliate still posting with it will start getting 401."
    )) return;
    setBusy(true);
    try {
      await api.post(`/organizations/${affiliate.id}/rotate-live-key`);
      toast.success("Live key rotated — notify the affiliate");
      await loadKeys();
      setReveal((r) => ({ ...r, live: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rotate failed");
    } finally { setBusy(false); }
  };

  const inviteVendor = async (e) => {
    e.preventDefault();
    if (!inviteEmail.includes("@")) return toast.error("Valid email required");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/organizations/${affiliate.id}/invite-vendor`,
        { email: inviteEmail, name: inviteName, send_email: true },
      );
      if (data.status === "linked_existing") {
        toast.success(`Linked existing affiliate login ${inviteEmail}`);
      } else {
        toast.success(`Invited ${inviteEmail} — invite email sent`);
      }
      setInviteEmail("");
      setInviteName("");
      loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const removeVendor = async (u) => {
    if (!confirm(`Remove ${u.email}?`)) return;
    try {
      await api.delete(`/organizations/${affiliate.id}/vendors/${u.id}`);
      toast.success(`Removed ${u.email}`);
      loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  // Iter-32: parse the state-cost editor. Supports three formats
  // interchangeably in the same paste, without any of them cannibalising
  // the others:
  //
  //   Legacy comma:    "AL:100, GA:80, TN:75"       (colon-separated pairs)
  //   Legacy semicolon:"AL=100; GA=80"              (equals-separated pairs)
  //   CSV rows:        "AL,100\nGA,80\nTN,75"       (comma-separated pairs, one per line)
  //
  // Strategy: if the input contains ANY newline OR semicolon, treat those
  // as the segment separators (so comma is free to be the intra-row k/v
  // separator). Otherwise fall back to comma-as-segment-separator (legacy).
  // Within each segment, allow comma, colon, equals, or whitespace between
  // the two-letter state and the numeric cost.
  const saveCosts = async () => {
    const raw = costsText || "";
    const hasRowSep = /[;\n]/.test(raw);
    const segments = raw
      .split(hasRowSep ? /[;\n]+/ : /,+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const parsed = {};
    const errs = [];
    for (const seg of segments) {
      const m = seg.match(/^([A-Za-z]{2})\s*[,:=\s]\s*(\d+(?:\.\d+)?)$/);
      if (!m) { errs.push(`Bad segment: "${seg}"`); continue; }
      parsed[m[1].toUpperCase()] = parseFloat(m[2]);
    }
    if (errs.length) return toast.error(errs[0]);
    if (!Object.keys(parsed).length) return toast.error("Nothing to save");
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/organizations/${affiliate.id}/state-costs`,
        { state_costs: parsed },
      );
      setSavedCosts(data.state_costs || {});
      toast.success(`Saved ${Object.keys(parsed).length} state cost${Object.keys(parsed).length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  // Iter-73.5: integration kit copy-for-email state — pre-rendered
  // plaintext + HTML the admin can paste into their own mail client.
  const [kitPreview, setKitPreview] = useState(null);
  const [kitPreviewOpen, setKitPreviewOpen] = useState(false);
  const [loadingKit, setLoadingKit] = useState(false);

  const loadKitPreview = async () => {
    setLoadingKit(true);
    try {
      const { data } = await api.get(
        `/organizations/${affiliate.id}/integration-kit`,
      );
      setKitPreview(data);
      setKitPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load kit preview");
    } finally { setLoadingKit(false); }
  };

  // Iter-73.6: synthetic canary lead — fires a sandbox lead through
  // the vendor's own key so ops can watch the full pipeline on day 1
  // without needing curl.
  const [canaryState, setCanaryState] = useState("AL");
  const [canaryAccident, setCanaryAccident] = useState("auto");
  const [canaryBusy, setCanaryBusy] = useState(false);
  const [canaryResult, setCanaryResult] = useState(null);

  const fireCanary = async () => {
    setCanaryBusy(true);
    setCanaryResult(null);
    try {
      const { data } = await api.post(
        `/organizations/${affiliate.id}/test-canary-lead`,
        {
          state: (canaryState || "AL").toUpperCase(),
          accident_type: canaryAccident || "auto",
        },
      );
      setCanaryResult(data);
      if (data.webhook_status === 200) {
        toast.success(`Canary fired — lead ${data.lead_id?.slice(0, 8)}…`);
      } else {
        toast.warning(
          `Canary posted, webhook returned ${data.webhook_status}`,
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Canary fire failed");
    } finally { setCanaryBusy(false); }
  };

  // Iter-33: one-click "send my vendor everything they need" email.
  // We deliberately do NOT call window.confirm() here — it blocks headless
  // browser automation and is jarring UX inside an already-modal surface.
  // The toast that fires on success provides all the confirmation needed;
  // an admin who fires this by mistake can simply not use the link.
  const sendKit = async () => {
    const to = kitEmail.trim() || affiliate.contact_email;
    if (!to || !to.includes("@")) return toast.error("Valid email required");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/organizations/${affiliate.id}/send-integration-kit`,
        { to_email: to },
      );
      toast.success(`Kit sent to ${data.to_email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  // Iter-66: persist the affiliate landing-page allowlist. Empty list is
  // meaningful (strict "no approvals" mode) — we always PATCH the exact
  // set the picker holds.
  const saveSlugs = async () => {
    setSavingSlugs(true);
    try {
      await api.patch(`/organizations/${affiliate.id}`, {
        allowed_landing_page_slugs: slugs,
      });
      setSavedSlugs(slugs);
      toast.success(
        slugs.length === 0
          ? "Allowlist cleared — affiliate has no approved pages"
          : `Approved ${slugs.length} landing page${slugs.length === 1 ? "" : "s"}`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSavingSlugs(false); }
  };

  const slugsDirty =
    slugs.length !== savedSlugs.length
    || slugs.some((s, i) => savedSlugs[i] !== s)
    || savedSlugs.some((s) => !slugs.includes(s));

  // Iter-68 — diff the profile draft against the last-persisted set so
  // the Save button starts disabled and re-arms on every real change.
  // Empty strings normalise to null on PATCH so clearing a field
  // actually clears it server-side.
  const profileDirty = useMemo(
    () => PROFILE_FIELDS.some((k) => (profile[k] || "") !== (savedProfile[k] || "")),
    [profile, savedProfile],
  );

  const saveProfile = async () => {
    if (!profileDirty) return;
    const trimmedName = (profile.name || "").trim();
    if (!trimmedName) return toast.error("Name is required");
    const email = (profile.contact_email || "").trim();
    if (email && !email.includes("@")) return toast.error("Contact email is invalid");

    const patch = {};
    for (const k of PROFILE_FIELDS) {
      const cur = (profile[k] || "").trim();
      const prev = (savedProfile[k] || "").trim();
      if (cur !== prev) {
        // The `EmailStr` type on the backend rejects empty strings —
        // send `null` when the admin clears the field.
        patch[k] = cur === "" ? null : cur;
      }
    }
    // Guard against an all-whitespace no-op that survived profileDirty.
    if (!Object.keys(patch).length) return;

    setSavingProfile(true);
    try {
      await api.patch(`/organizations/${affiliate.id}`, patch);
      const next = { ...profile,
        name: trimmedName,
        contact_email: email,
        contact_phone: (profile.contact_phone || "").trim(),
        website: (profile.website || "").trim(),
        notes: (profile.notes || "").trim(),
      };
      setProfile(next);
      setSavedProfile(next);
      toast.success("Profile updated");
      onSaved?.(next);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally { setSavingProfile(false); }
  };

  // Iter-33: toggle A/B split. When ON, traffic to /lp/pi with this vendor's
  // ?aff= param 50/50 splits between /lp/personal-injury and /lp/free-case-review.
  const toggleAbSplit = async () => {
    const next = !abSplit;
    setAbSplit(next); // optimistic
    try {
      await api.patch(`/organizations/${affiliate.id}/ab-split`, { enabled: next });
      toast.success(next ? "A/B split enabled" : "A/B split disabled");
    } catch (err) {
      setAbSplit(!next); // rollback
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="affiliate-manage-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white border-4 border-black w-full max-w-3xl p-6 brut-shadow-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-mono">Affiliate ops</div>
            <h2 className="font-chivo font-black text-2xl uppercase tracking-tighter leading-tight" data-testid="affiliate-manage-title">
              Manage {savedProfile.name || affiliate.name}
            </h2>
            <div className="text-xs text-zinc-600 mt-1">
              {savedProfile.contact_email || affiliate.contact_email || "no email on file"} · {affiliate.public_source_id}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="affiliate-manage-close"
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Iter-68 — editable profile + contact section. Sits above the
             API keys block so it's the first thing the admin sees on
             open. Empty inputs clear the field server-side (sent as
             `null`) which lets ops truly remove an outdated contact. */}
        <div className="mb-6" data-testid="affiliate-profile-section">
          <div className="flex items-center gap-2 mb-3">
            <IdentificationCard size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Profile &amp; contact
            </div>
            <div className="label-mono ml-auto">editable</div>
          </div>
          <div className="border border-black p-4 bg-zinc-50 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="label-mono">Affiliate name</label>
              <input
                type="text"
                data-testid="affiliate-profile-name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full border border-black px-3 py-2 mt-1"
                placeholder="Affiliate / firm name"
              />
            </div>
            <div>
              <label className="label-mono">Contact email</label>
              <input
                type="email"
                data-testid="affiliate-profile-email"
                value={profile.contact_email}
                onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                className="w-full border border-black px-3 py-2 mt-1 font-mono text-[12px]"
                placeholder="ops@affiliate.com"
              />
            </div>
            <div>
              <label className="label-mono">Contact phone</label>
              <input
                type="tel"
                data-testid="affiliate-profile-phone"
                value={profile.contact_phone}
                onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                className="w-full border border-black px-3 py-2 mt-1 font-mono text-[12px]"
                placeholder="+1 415 555 0100"
              />
            </div>
            <div className="md:col-span-2">
              <label className="label-mono">Website</label>
              <input
                type="text"
                data-testid="affiliate-profile-website"
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                className="w-full border border-black px-3 py-2 mt-1 font-mono text-[12px]"
                placeholder="growmyfirmonline.com"
              />
            </div>
            <div className="md:col-span-2">
              <label className="label-mono">Internal notes</label>
              <textarea
                rows={3}
                data-testid="affiliate-profile-notes"
                value={profile.notes}
                onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                className="w-full border border-black px-3 py-2 mt-1 text-sm"
                placeholder="Anything ops should know — payment terms, ad-platform quirks, etc."
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={saveProfile}
                disabled={savingProfile || !profileDirty}
                data-testid="affiliate-profile-save"
                className="bg-black text-white border border-black px-3 py-1.5 uppercase text-[10px] tracking-widest font-bold disabled:opacity-40"
              >
                {savingProfile ? "Saving…" : profileDirty ? "Save profile" : "Saved"}
              </button>
              <div className="text-[10px] text-zinc-600 leading-relaxed flex-1 min-w-[240px]">
                Emptying a field <b>clears</b> it server-side. Name cannot be empty.
                Emergent Login <span className="font-mono">public_source_id</span> and API keys are locked — rotate keys below.
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Key size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              API keys
            </div>
          </div>
          {!keys ? (
            <div className="label-mono text-zinc-500">Loading…</div>
          ) : (
            <div className="space-y-3">
              <KeyRow
                label="LIVE key — delivers to buyer"
                value={keys.live_api_key}
                tint="bg-emerald-900 text-white"
                revealed={reveal.live}
                onToggle={() => setReveal({ ...reveal, live: !reveal.live })}
                onCopy={copy}
                onRotate={rotateLive}
                busy={busy}
                testId="live-key"
              />
              <KeyRow
                label="SANDBOX key — never delivers"
                value={keys.sandbox_api_key}
                tint="bg-sky-800 text-white"
                revealed={reveal.sandbox}
                onToggle={() => setReveal({ ...reveal, sandbox: !reveal.sandbox })}
                onCopy={copy}
                onRotate={rotateSandbox}
                busy={busy}
                testId="sandbox-key"
              />
              <div className="text-[10px] text-zinc-600 leading-relaxed">
                <Flask size={10} className="inline mr-1" />
                Leads posted with the SANDBOX key are forcibly marked <code className="font-mono">is_test=true</code>
                and skip delivery — the affiliate can test their integration end-to-end without hitting the buyer&apos;s inbox.
              </div>
            </div>
          )}
        </div>

        <div className="mb-6 border-t-2 border-black pt-5" data-testid="cost-schedule-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="font-chivo font-black uppercase tracking-tight">
              Affiliate cost schedule
            </div>
            <div className="label-mono ml-auto">what WE pay THEM per accepted lead</div>
          </div>
          <label className="label-mono">
            Per-state cost — one per line, comma-separated, or paste a CSV of
            <span className="font-mono mx-1">STATE,cost</span> rows
          </label>
          <textarea
            data-testid="state-costs-input"
            value={costsText}
            onChange={(e) => setCostsText(e.target.value)}
            rows={6}
            className="w-full border border-black px-3 py-2 mt-1 font-mono text-[11px]"
            placeholder={"AL:100, GA:80, TN:75\n\nOR paste CSV:\nAL,100\nGA,80\nTN,75\nFL,90"}
          />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              type="button"
              onClick={saveCosts}
              disabled={busy}
              data-testid="state-costs-save"
              className="bg-black text-white border border-black px-3 py-1.5 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save cost schedule"}
            </button>
            <div className="text-[10px] text-zinc-500 leading-relaxed flex-1 min-w-[240px]">
              Affiliates <b>never see</b> the buyer&apos;s sell price — only their own cost. Supports both
              <span className="font-mono mx-1">AL:100</span> and
              <span className="font-mono mx-1">AL,100</span> formats.
            </div>
          </div>
          {Object.keys(savedCosts).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="state-costs-saved">
              {Object.entries(savedCosts).sort().map(([st, c]) => (
                <span key={st} className="border border-black px-2 py-0.5 text-[10px] font-mono">
                  {st}: ${Number(c).toFixed(2)}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6 border-t-2 border-black pt-5" data-testid="approved-lps-section">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Approved landing pages
            </div>
            <div className="label-mono ml-auto">
              controls where <code className="font-mono">?aff={affiliate.public_source_id}</code> is accepted
            </div>
          </div>
          <div className="border border-black p-4 bg-zinc-50">
            <div className="text-[11px] text-zinc-700 leading-relaxed mb-3 border-l-2 border-emerald-600 pl-3 bg-emerald-50/60 py-2 -mx-1">
              <b>Tip:</b> an affiliate can be approved for a buyer's <b>custom
              landing page</b>, the <b>generic verticals</b>, or both at once.
              Check every page you want this affiliate to send traffic to below.
              The routing engine still evaluates <b>every</b> lead against
              buyer criteria — matching leads always reach the right buyer,
              no matter which page they came from.
            </div>
            <div className="mb-3">
              <AllPagesToggle kind="affiliate" id={affiliate.id} value={allPages} testId="aff-all-pages"
                              onChange={(d) => { setAllPages(d.all_pages); setSlugs(d.slugs || []); setSavedSlugs(d.slugs || []); }} />
            </div>
            <LandingPageAllowlistPicker
              value={slugs}
              onChange={setSlugs}
              testIdPrefix="aff-lp"
            />
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <button
                type="button"
                onClick={saveSlugs}
                disabled={savingSlugs || !slugsDirty}
                data-testid="approved-lps-save"
                className="bg-black text-white border border-black px-3 py-1.5 uppercase text-[10px] tracking-widest font-bold disabled:opacity-40"
              >
                {savingSlugs ? "Saving…" : slugsDirty ? "Save approved pages" : "Saved"}
              </button>
              <div className="text-[10px] text-zinc-600 leading-relaxed flex-1 min-w-[240px]">
                Any traffic driven with <span className="font-mono">?aff={affiliate.public_source_id}</span> to a
                slug NOT approved above is rejected with a <b>403</b> at intake — the lead is never stored.
                Direct traffic (no <span className="font-mono">?aff</span>) is unaffected.
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 border-t-2 border-black pt-5" data-testid="integration-kit-section">
          <div className="flex items-center gap-2 mb-3">
            <PaperPlaneTilt size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Integration kit email
            </div>
            <div className="label-mono ml-auto">tracking links · api keys · curl</div>
          </div>
          <div className="border border-black p-4 bg-zinc-50">
            <div className="text-xs text-zinc-700 mb-3 leading-relaxed">
              Send this affiliate an email with all 6 landing-page tracking links
              (pre-filled with their <span className="font-mono">?aff={affiliate.public_source_id}</span>),
              their live + sandbox API keys, a copy-paste curl example, and a link to their dashboard.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="email"
                data-testid="integration-kit-email"
                value={kitEmail}
                onChange={(e) => setKitEmail(e.target.value)}
                placeholder={affiliate.contact_email || "affiliate@example.com"}
                className="flex-1 border border-black px-3 py-2 font-mono text-[11px] min-w-[220px]"
              />
              <button
                type="button"
                onClick={sendKit}
                disabled={busy}
                data-testid="integration-kit-send"
                className="bg-black text-white border border-black px-3 py-2 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2"
              >
                <PaperPlaneTilt size={11} weight="bold" />
                {busy ? "Sending…" : "Send from LeadFlow"}
              </button>
              <button
                type="button"
                onClick={loadKitPreview}
                disabled={loadingKit}
                data-testid="integration-kit-preview"
                className="border-2 border-black bg-white px-3 py-2 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2 hover:bg-black hover:text-white"
                title="Preview the kit and copy it for pasting into your personal email"
              >
                <Copy size={11} weight="bold" />
                {loadingKit ? "Loading…" : "Copy for personal email"}
              </button>
            </div>
            <div className="text-[10.5px] text-zinc-600 mt-2 leading-relaxed">
              <b>Send from LeadFlow</b> — fires the email through Resend from
              our server. <b>Copy for personal email</b> — opens a preview and
              gives you a one-click copy so you can paste it into Gmail, Outlook,
              or your CRM and send it from your own address.
            </div>
          </div>
        </div>

        {kitPreviewOpen && kitPreview && (
          <IntegrationKitPreview
            kit={kitPreview}
            onClose={() => setKitPreviewOpen(false)}
          />
        )}

        <div className="mb-6 border-t-2 border-black pt-5" data-testid="canary-section">
          <div className="flex items-center gap-2 mb-3">
            <Flask size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Fire test canary
            </div>
            <div className="label-mono ml-auto">
              synthetic sandbox lead · uses this affiliate's key
            </div>
          </div>
          <div className="border border-black p-4 bg-zinc-50">
            <div className="text-xs text-zinc-700 mb-3 leading-relaxed">
              Posts a synthetic lead through the affiliate's <b>sandbox key</b> to
              exercise the whole prod pipeline (validation → routing → delivery)
              end-to-end. Marked <code>is_test=true</code>, never reaches a
              buyer. Perfect for day-1 smoke testing.
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="label-mono">State</label>
                <input
                  data-testid="canary-state"
                  value={canaryState}
                  onChange={(e) => setCanaryState(e.target.value)}
                  maxLength={2}
                  className="w-16 border border-black px-2 py-2 mt-1 font-mono uppercase"
                  placeholder="AL"
                />
              </div>
              <div>
                <label className="label-mono">Accident type</label>
                <select
                  data-testid="canary-accident-type"
                  value={canaryAccident}
                  onChange={(e) => setCanaryAccident(e.target.value)}
                  className="border border-black px-2 py-2 mt-1 bg-white"
                >
                  <option value="auto">auto</option>
                  <option value="truck">truck</option>
                  <option value="motorcycle">motorcycle</option>
                  <option value="rideshare">rideshare</option>
                  <option value="slip_and_fall">slip_and_fall</option>
                </select>
              </div>
              <button
                type="button"
                onClick={fireCanary}
                disabled={canaryBusy}
                data-testid="canary-fire"
                className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Flask size={11} weight="bold" />
                {canaryBusy ? "Firing…" : "Fire canary"}
              </button>
            </div>
            {canaryResult && (
              <div
                className={`mt-4 border-2 p-3 text-xs ${
                  canaryResult.webhook_status === 200
                    ? "border-emerald-700 bg-emerald-50"
                    : "border-amber-700 bg-amber-50"
                }`}
                data-testid="canary-result"
              >
                <div className="font-chivo font-black uppercase tracking-tight mb-1">
                  Webhook returned {canaryResult.webhook_status}
                  {" · "}
                  {canaryResult.webhook_response?.status || "no status"}
                </div>
                {canaryResult.lead_id && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span>
                      Lead ID:{" "}
                      <code className="font-mono">
                        {canaryResult.lead_id}
                      </code>
                    </span>
                    <a
                      href={canaryResult.lead_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="canary-lead-link"
                      className="border border-black bg-white px-2 py-1 uppercase text-[10px] tracking-widest font-bold hover:bg-black hover:text-white"
                    >
                      Open lead →
                    </a>
                  </div>
                )}
                <div className="mt-2 text-[10.5px] text-zinc-600">
                  <b>is_test:</b>{" "}
                  {String(!!canaryResult.webhook_response?.is_test)}
                  {" · "}
                  <b>state:</b> {canaryResult.sent_payload?.state}
                  {" · "}
                  <b>accident_type:</b>{" "}
                  {canaryResult.sent_payload?.accident_type}
                </div>
              </div>
            )}
          </div>
        </div>

        <MarketplaceCommissionField affiliateId={affiliate.id} />

        <div className="mb-6 border-t-2 border-black pt-5" data-testid="traffic-sources-section">
          <div className="flex items-center gap-2 mb-3">
            <Broadcast size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Landing pages this affiliate pushes to
            </div>
            <div className="label-mono ml-auto">last 30 days · read-only</div>
          </div>
          <TrafficSourcesPanel
            scope="affiliate"
            orgId={affiliate.id}
            testIdPrefix="affiliate-traffic-sources"
          />
        </div>

        <div className="mb-6 border-t-2 border-black pt-5" data-testid="ab-split-section">
          <div className="flex items-center gap-2 mb-3">
            <ShuffleAngular size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Landing-page A/B split
            </div>
            <div className="label-mono ml-auto">split traffic to /lp/pi</div>
          </div>
          <div className="border border-black p-4 bg-zinc-50 flex items-center gap-4 flex-wrap">
            <button
              type="button"
              role="switch"
              aria-checked={abSplit}
              onClick={toggleAbSplit}
              data-testid="ab-split-toggle"
              className={`relative w-14 h-7 border-2 border-black transition-colors ${abSplit ? "bg-emerald-600" : "bg-white"}`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 bg-white border-2 border-black transition-all ${abSplit ? "left-7" : "left-0.5"}`}
              />
            </button>
            <div className="flex-1 min-w-[220px]">
              <div className="font-bold text-xs uppercase tracking-widest" data-testid="ab-split-state">
                {abSplit ? "Enabled — 50/50 split active" : "Disabled — traffic goes to /lp/personal-injury only"}
              </div>
              <div className="text-[11px] text-zinc-600 leading-relaxed mt-1">
                Point your ads at
                <code className="font-mono mx-1 bg-white border border-zinc-300 px-1">/lp/pi?aff={affiliate.public_source_id}</code>.
                When enabled, visitors are sticky-bucketed 50/50 between
                <code className="font-mono mx-1 bg-white border border-zinc-300 px-1">/lp/personal-injury</code>
                and
                <code className="font-mono mx-1 bg-white border border-zinc-300 px-1">/lp/free-case-review</code>.
                Watch the &quot;A/B — variants head-to-head&quot; callout on the affiliate dashboard for which one converts better.
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 border-t-2 border-black pt-5">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tight">
              Affiliate logins ({vendors.length})
            </div>
          </div>
          <form onSubmit={inviteVendor} className="border border-black p-4 bg-zinc-50">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mono">Email</label>
                <input
                  data-testid="invite-vendor-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full border border-black px-3 py-2 mt-1"
                  placeholder="kristina@growmyfirmonline.com"
                />
              </div>
              <div>
                <label className="label-mono">Name (optional)</label>
                <input
                  data-testid="invite-vendor-name"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="w-full border border-black px-3 py-2 mt-1"
                  placeholder="Kristina Herd"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="invite-vendor-submit"
              className="mt-3 bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2"
            >
              <UserPlus size={12} weight="bold" />
              {busy ? "Sending…" : "Invite affiliate login"}
            </button>
          </form>

          {vendors.length > 0 && (
            <table className="w-full text-xs border border-black mt-4" data-testid="vendor-list">
              <thead>
                <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                  <th className="text-left py-2 px-3">Email</th>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.id} className="border-b border-zinc-200" data-testid={`vendor-row-${v.id}`}>
                    <td className="py-2 px-3 font-mono">{v.email}</td>
                    <td className="py-2 px-3">{v.name}</td>
                    <td className="py-2 px-3">
                      <span className="border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest">
                        {v.status}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Iter-69: admin-initiated password reset for this
                             vendor login. Same backend guard as Employees. */}
                        <button
                          onClick={() => setPwTarget(v)}
                          data-testid={`vendor-set-password-${v.id}`}
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                          title="Set a new password for this affiliate login"
                        >
                          <Key size={10} className="inline" /> Password
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => removeVendor(v)}
                            data-testid={`vendor-remove-${v.id}`}
                            className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white"
                          >
                            <Trash size={10} className="inline" /> Unlink
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border border-black px-4 py-2 uppercase text-xs tracking-widest hover:bg-zinc-100"
          >
            Close
          </button>
        </div>
      </div>
      {pwTarget && (
        <SetPasswordDialog
          user={pwTarget}
          onClose={() => setPwTarget(null)}
          onSaved={loadVendors}
          testIdPrefix="vendor-set-pw"
        />
      )}
    </div>
  );
}
